import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./BookNowForm.css";
import "./ServiceQuoteForm.css";
import ServiceCategoryFields from "./ServiceCategoryFields";
import { CATEGORIES, MINIMUM_BOOKING, calculateQuote } from "../utils/calculateTotal.js";
import apiClient from "../utils/apiClient.js";
import BookingConfirmationModal from "./BookingConfirmationModal.js";
import TermsAcceptanceModal from "./TermsAcceptanceModal.js";
import Spinner from "./Spinner.js";
import useSeo, { buildBreadcrumbs } from "../hooks/useSeo.js";

const EFT_NOTICE =
  "Online EFT payment integration is currently under development. Our team will contact you with payment instructions after your booking has been confirmed. When making your bank transfer, please include your booking or invoice number as the payment reference.";

// Mirrors ServiceQuoteForm.js's contact validation (and the backend's own regex at the trust
// boundary) — this form previously relied only on the browser's native type="email"/type="tel"
// checks, which are far looser (most browsers accept "a@b" as a valid email) and give no inline
// feedback of their own.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+\-\s()]{6,20}$/;

// Perth/WA runs on AWST year-round (UTC+8, no daylight saving) — these mirror the backend's
// service-hours window exactly so a customer sees the restriction before submitting, not after.
const SERVICE_TIME_MIN = "07:00";
const SERVICE_TIME_MAX = "19:00";

// 15-minute options across the full day, not a sparse fixed-slot list — a customer can still pick
// any point across the whole 7am-7pm window, this just presents it as an easy, well-labeled
// dropdown instead of the native <input type="time"> picker (whose rendering varies wildly across
// browsers and was never actually styled to match the rest of this form).
const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const TIME_OPTIONS = (() => {
  const options = [];
  for (let minutes = toMinutes(SERVICE_TIME_MIN); minutes <= toMinutes(SERVICE_TIME_MAX); minutes += 15) {
    const hours24 = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const value = `${String(hours24).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
    const period = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    options.push({ value, label: `${hours12}:${String(mins).padStart(2, "0")} ${period}` });
  }
  return options;
})();

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

const emptyDetails = () => {
  const details = {};
  CATEGORIES.forEach((category) => {
    details[category.key] = {};
    category.fields.forEach((field) => {
      details[category.key][field.key] = 0;
    });
  });
  return details;
};

// seoEnabled: true when reached as its own standalone, linkable page (mirrors ServiceQuoteForm's
// pattern) — false when embedded elsewhere (the Homepage's booking section), so it doesn't
// override that page's own title/canonical/structured-data.
const BookingForm = ({ prefillContact, prefillCategories, prefillDetails, seoEnabled = false }) => {
  const [selectedCategories, setSelectedCategories] = useState(prefillCategories || []);
  const [details, setDetails] = useState(emptyDetails);
  const [categoryError, setCategoryError] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  // Holds the validated form payload while the customer reviews the Terms & Conditions gate —
  // the actual POST only fires once they click Accept, never on the form's own submit event.
  const [pendingBooking, setPendingBooking] = useState(null);
  const [showTerms, setShowTerms] = useState(false);
  const [contactErrors, setContactErrors] = useState({});
  const formRef = useRef(null);
  const navigate = useNavigate();

  useSeo({
    title: "Book a Cleaning Service – Carpet, Upholstery, Curtains & Windows",
    description:
      "Book carpet & rug, upholstery & mattress, curtains & blinds, or window glass cleaning in Perth, Western Australia. Available 7am-7pm, 7 days a week.",
    path: "/book",
    structuredData: buildBreadcrumbs([
      { name: "Home", path: "/" },
      { name: "Book Now", path: "/book" },
    ]),
    enabled: seoEnabled,
  });

  const backToServices = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/services");
    }
  };

  useEffect(() => {
    if (prefillDetails) {
      setDetails((prev) => {
        const next = { ...prev };
        Object.entries(prefillDetails).forEach(([categoryKey, fields]) => {
          next[categoryKey] = { ...next[categoryKey], ...fields };
        });
        return next;
      });
    }
    // Prefill only applies once, from whatever the visitor already calculated on the quote form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCategory = (key) => {
    setCategoryError("");
    setSelectedCategories((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleSelectAll = () => {
    setCategoryError("");
    setSelectedCategories((prev) => (prev.length === CATEGORIES.length ? [] : CATEGORIES.map((c) => c.key)));
  };

  const handleFieldChange = (categoryKey, fieldKey, value) => {
    setDetails((prev) => ({ ...prev, [categoryKey]: { ...prev[categoryKey], [fieldKey]: value } }));
  };

  const activeDetails = {};
  selectedCategories.forEach((key) => {
    activeDetails[key] = details[key];
  });
  const quoteResult = calculateQuote(activeDetails);

  // Validates the form and, if it passes, holds the payload and shows the Terms & Conditions
  // gate — the actual booking POST only happens from handleAcceptTerms below, never directly from
  // this submit event. This is what gives us clear evidence the customer saw and accepted the
  // terms for this specific booking, not just at some point in the past.
  const handleSubmit = (e) => {
    e.preventDefault();

    const elements = e.target.elements;
    const email = elements.email.value.trim();
    const phone = elements.phone.value.trim();
    const nextContactErrors = {};
    if (!EMAIL_REGEX.test(email)) nextContactErrors.email = "Enter a valid email address.";
    if (!PHONE_REGEX.test(phone)) nextContactErrors.phone = "Enter a valid phone number.";
    setContactErrors(nextContactErrors);
    if (Object.keys(nextContactErrors).length > 0) return;

    if (selectedCategories.length === 0) {
      setCategoryError("Select at least one service to book.");
      return;
    }
    if (!quoteResult.meetsMinimum) {
      return;
    }

    setPendingBooking({
      fullName: elements.full_name.value,
      email: elements.email.value,
      phone: elements.phone.value,
      serviceType: selectedCategories,
      details: activeDetails,
      serviceDate: elements.service_date.value,
      serviceTime: elements.service_time.value,
      address: elements.address.value,
      instructions: elements.instructions.value,
      paymentMethod,
      quotedTotal: quoteResult.total,
      website: elements.website.value,
    });
    setShowTerms(true);
  };

  const submitBooking = async (payload) => {
    setSubmitting(true);
    try {
      const res = await apiClient.post("/bookings", { ...payload, termsAccepted: true });
      setConfirmation({
        status: "success",
        title: "Booking Request Received!",
        message: `Thank you, ${payload.fullName.split(" ")[0]}. We've received your booking and you'll be notified soon with your confirmation and next steps.`,
        summary: [
          { label: "Booking Reference", value: `#${res.data.id}` },
          {
            label: "Service",
            value: CATEGORIES.filter((c) => payload.serviceType.includes(c.key)).map((c) => c.label).join(", "),
          },
          { label: "Date", value: formatDate(payload.serviceDate) },
          { label: "Time", value: payload.serviceTime },
          { label: "Estimated total", value: `$${Number(payload.quotedTotal).toFixed(2)}` },
        ],
      });
      formRef.current?.reset();
      setSelectedCategories([]);
      setDetails(emptyDetails());
      setPaymentMethod("");
    } catch (err) {
      // 409 (time slot already booked) and 422 (below minimum) carry a specific, actionable
      // message from the server — show that instead of a generic failure.
      const serverMessage = err.response?.data?.error;
      setConfirmation({
        status: "error",
        title: err.response?.status === 409 ? "That Time Is Already Booked" : "We Couldn't Submit Your Booking",
        message:
          serverMessage ||
          "Something went wrong on our end. Please try again in a moment, or contact us directly if the issue continues.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptTerms = () => {
    setShowTerms(false);
    if (pendingBooking) submitBooking(pendingBooking);
    setPendingBooking(null);
  };

  const handleDeclineTerms = () => {
    setShowTerms(false);
    setPendingBooking(null);
  };

  const formContent = (
    <form className="booking-form" onSubmit={handleSubmit} ref={formRef}>
      <h2>Book Your Cleaning Service</h2>

      <div className="form-group">
        <label>Full Name:</label>
        <input
          type="text"
          name="full_name"
          placeholder="Enter your full name"
          defaultValue={prefillContact?.fullName || ""}
          required
        />
      </div>
      <div className="form-group">
        <label>Email Address:</label>
        <input
          type="email"
          name="email"
          placeholder="Enter your email"
          defaultValue={prefillContact?.email || ""}
          required
        />
        {contactErrors.email && <p className="field-error">{contactErrors.email}</p>}
      </div>
      <div className="form-group">
        <label>Phone Number:</label>
        <input
          type="tel"
          name="phone"
          placeholder="Enter your phone number"
          defaultValue={prefillContact?.phone || ""}
          required
        />
        {contactErrors.phone && <p className="field-error">{contactErrors.phone}</p>}
      </div>

      <div className="quote-form">
        <div className="form-group">
          <label>Which service(s) would you like to book?</label>
          <div className="quote-tabs" role="group" aria-label="Select the services you'd like to book">
            <button
              type="button"
              className={`quote-tab-btn ${selectedCategories.length === CATEGORIES.length ? "active" : ""}`}
              onClick={toggleSelectAll}
            >
              Select all
            </button>
            {CATEGORIES.map((category) => (
              <button
                key={category.key}
                type="button"
                className={`quote-tab-btn ${selectedCategories.includes(category.key) ? "active" : ""}`}
                aria-pressed={selectedCategories.includes(category.key)}
                onClick={() => toggleCategory(category.key)}
              >
                {category.label}
              </button>
            ))}
          </div>
          {categoryError && <p className="field-error">{categoryError}</p>}
        </div>

        {CATEGORIES.filter((category) => selectedCategories.includes(category.key)).map((category) => (
          <ServiceCategoryFields
            key={category.key}
            category={category}
            values={details[category.key]}
            onChange={handleFieldChange}
          />
        ))}

        {selectedCategories.length > 0 && (
          <>
            <div className="quote-breakdown">
              {quoteResult.lineItems.map((item) => (
                <div className="quote-line" key={`${item.category}-${item.field}`}>
                  <span>{item.label} × {item.quantity}</span>
                  <span>${item.amount.toFixed(2)}</span>
                </div>
              ))}
              <div className="quote-line quote-line-subtotal">
                <span>Service subtotal</span>
                <span>${quoteResult.serviceSubtotal.toFixed(2)}</span>
              </div>
              {quoteResult.quantityDiscounts > 0 && (
                <div className="quote-line quote-line-discount">
                  <span>Quantity discounts</span>
                  <span>-${quoteResult.quantityDiscounts.toFixed(2)}</span>
                </div>
              )}
              {quoteResult.orderDiscount > 0 && (
                <div className="quote-line quote-line-discount">
                  <span>Order discount ({(quoteResult.orderDiscountRate * 100).toFixed(1)}%)</span>
                  <span>-${quoteResult.orderDiscount.toFixed(2)}</span>
                </div>
              )}
            </div>
            <h3>Total Quote: ${quoteResult.total.toFixed(2)}</h3>
            <p className={`quote-status ${quoteResult.meetsMinimum ? "quote-status-ok" : "quote-status-warn"}`}>
              {quoteResult.meetsMinimum
                ? `✓ Meets our $${MINIMUM_BOOKING} minimum booking`
                : `Add $${(MINIMUM_BOOKING - quoteResult.total).toFixed(2)} more to reach our $${MINIMUM_BOOKING} minimum booking`}
            </p>
            <p className="quote-disclaimer">
              Quoted prices are estimates only and may change if the actual job differs from the
              original booking, including where payment has already been made.
            </p>
          </>
        )}
      </div>

      <div className="form-group">
        <label>Service Date:</label>
        <input type="date" name="service_date" required />
      </div>
      <div className="form-group">
        <label>Service Time:</label>
        <select name="service_time" defaultValue="" required className="time-select">
          <option value="" disabled>
            Select a time
          </option>
          {TIME_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="field-hint">We're on the job every day from 7:00 AM to 7:00 PM — pick whatever time suits you best.</p>
      </div>
      <div className="form-group">
        <label>Address:</label>
        <textarea name="address" placeholder="Enter your address" required></textarea>
      </div>
      <div className="form-group">
        <label>Additional Instructions:</label>
        <textarea name="instructions" placeholder="Please specify the type of stain (e.g., mud/dirt, food/coffee spills, pet accidents, or gum) so we can apply the correct treatment."></textarea>
      </div>
      <div className="form-group">
        <label>Payment Method:</label>
        <select
          name="payment_method"
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          required
        >
          <option value="">Select Payment Method</option>
          <option value="cash">Cash</option>
          <option value="eft">Bank Transfer</option>
        </select>
        {paymentMethod === "eft" && <p className="eft-notice">{EFT_NOTICE}</p>}
      </div>

      {/* Honeypot — hidden from real users, left empty by them; a filled value signals a bot. */}
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input type="text" id="website" name="website" tabIndex="-1" autoComplete="off" />
      </div>

      <button type="submit" disabled={submitting || selectedCategories.length === 0 || !quoteResult.meetsMinimum}>
        {submitting ? (
          <>
            <Spinner size="sm" inline /> Submitting…
          </>
        ) : (
          "Submit Booking Request"
        )}
      </button>
    </form>
  );

  const modal = (
    <>
      <BookingConfirmationModal
        status={confirmation?.status}
        title={confirmation?.title}
        message={confirmation?.message}
        summary={confirmation?.summary}
        onClose={() => setConfirmation(null)}
      />
      <TermsAcceptanceModal open={showTerms} onAccept={handleAcceptTerms} onDecline={handleDeclineTerms} />
    </>
  );

  // Standalone route (reached from a direct link or a Services/quote handoff): show a matching
  // photo alongside the form, same wrapper pattern as ServiceQuoteForm's standalone mode. Embedded
  // usage (the Homepage's booking section) skips this wrapper since the parent page already
  // supplies its own layout.
  if (!seoEnabled) {
    return (
      <>
        {formContent}
        {modal}
      </>
    );
  }

  return (
    <>
      <div className="quote-page-back-wrap">
        <button type="button" className="quote-back-btn" onClick={backToServices}>
          ← Back to Services
        </button>
      </div>
      <div className="quote-page-layout">
        <div className="quote-page-image">
          <img
            src={require("../assets/carpet&rug.jpg")}
            alt="Aeti cleaning technician arriving for a scheduled booking in Perth"
          />
        </div>
        <div className="quote-page-panel">{formContent}</div>
      </div>
      {modal}
    </>
  );
};

export default BookingForm;
